import React, { useState, useEffect } from "react";
import "../../styles/Reports.scss";
import Modal from "react-modal";
import Select from "react-select";
import { getAllClients, getDailyReport } from "../../services/api";
import ExcelJS from "exceljs";
import moment from "moment";

Modal.setAppElement("#root");

const ITEMS_PER_PAGE = 10;

function MonthlyReport() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [reportData, setReportData] = useState([]);

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Generate month options
  const monthOptions = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  // Generate year options (current year and 5 years back)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => ({
    value: currentYear - i,
    label: (currentYear - i).toString(),
  }));



  const getDateRange = () => {
    if (!selectedMonth || !selectedYear) return { startDate: "", endDate: "" };
    // Format the month value with padding (e.g., "01" for January)
    const monthStr = selectedMonth.value.toString().padStart(2, '0');
    const startDate = moment(`${selectedYear.value}-${monthStr}-01`).format('YYYY-MM-DD');
    const endDate = moment(startDate).endOf('month').format('YYYY-MM-DD');

    return { startDate, endDate };
  };

  const handleExport = async () => {
    
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Monthly Report");

    // Add header row
    worksheet.columns = [
      { header: "S.NO", key: "sno" },
      { header: "Recruiter", key: "recruiter_name" },
      { header: "No.of Submissions", key: "no_of_submissions" },
      { header: "Client", key: "client_name" },
      { header: "No. of Requirements Worked", key: "no_of_requirements_worked" },
      { header: "Candidates Joined", key: "candidates_joined" },
    ];

    // Add data rows
    reportData.forEach((item, index) => {
      worksheet.addRow({
        sno: index + 1,
        recruiter_name: item.recruiter_name || "-",
        no_of_submissions: item.no_of_submissions || "-",
        client_name: item.client_name || "-",
        no_of_requirements_worked: item.no_of_requirements_worked || "-",
        candidates_joined: item.candidates_joined || "-",
        
      });
    });

    // Create a Blob from the workbook
    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "MonthlyReport.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pageCount) return;
    setPage(newPage);
  };

  // Reset to page 1 on search
  React.useEffect(() => {
    setPage(1);
  }, [selectedClient, selectedMonth, selectedYear, reportData.length]);

  // for final summary filter along with pagination
  const filteredData = reportData;

  const pageCount = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const paginatedData = filteredData.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  // To get All clients
  const fetchClients = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getAllClients();
      if (res && res.status === "S" && Array.isArray(res.result_info)) {
        setClients(res.result_info);
      } else {
        setError(res?.error_info || "Failed to fetch clients");
        setClients([]);
      }
    } catch (err) {
      setError("Failed to fetch clients. Please try again.");
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  // To get final summary data
  const fetchDailyReport = async () => {
    setLoading(true);
    setError("");
    
    // Check if both month and year are selected
    if (!selectedMonth || !selectedYear) {
      setLoading(false);
      // setReportData([]);
      // return;
      
    }

    if(selectedMonth && !selectedYear){
      setReportData([]);
      return;
    }
    if(!selectedMonth && selectedYear){
      setReportData([]);
      return;
    }
    
    try {
      const { startDate, endDate } = getDateRange();
      
      const res = await getDailyReport(
        selectedClient?.value || "",
        "",
        startDate,
        endDate 
      );
      if (res && res.status === "S" && Array.isArray(res.result_info)) {
        setReportData(res.result_info);
      } else {
        setError(res?.message || "Failed to fetch report data");
        setReportData([]);
      }
    } catch (err) {
      setError("Failed to fetch report data. Please try again.");
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch clients on component mount
  useEffect(() => {
    fetchClients();
  }, []);

  // Fetch report data when client, month, or year change
  useEffect(() => {
    // Always fetch report data (API will be triggered by default)
    fetchDailyReport();
  }, [selectedClient, selectedMonth, selectedYear]);

  // Convert clients to react-select options
  const clientOptions = clients.map((client) => ({
    value: client.id,
    label: client.name,
  }));

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };


   function getPageNumbers(currentPage, totalPages, maxPagesToShow = 5) {
    const pages = [];

    const half = Math.floor(maxPagesToShow / 2);
    let start = Math.max(currentPage - half, 1);
    let end = start + maxPagesToShow - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(end - maxPagesToShow + 1, 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }
  return (
    <div className="reports-page">
      <div className="reports-header-row">
        <div className="reports-title-group">
          <h1>Monthly Report</h1>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button 
            className="export-btn" 
            onClick={handleExport}
            
          >
            Export
          </button>
        </div>
      </div>
      <div className="reports-table-controls">
        <div className="filters-row">
          <div className="select-wrapper">
            <Select
              placeholder="Select a Client"
              value={selectedClient}
              onChange={setSelectedClient}
              options={clientOptions}
              styles={{
                control: (base) => ({
                  ...base,
                  minWidth: 220,
                  borderRadius: 6,
                  fontSize: "1rem",
                  background: "#fff",
                  border: "1.5px solid #d1d5db",
                }),
                menu: (base) => ({ ...base, zIndex: 20 }),
              }}
              isClearable
              isSearchable
            />
          </div>
          <div className="date-filters">
            <div className="date-input-wrapper">
              {!selectedMonth && (
                <span className="placeholder-text">Month</span>
              )}
              <Select
                placeholder="Select Month"
                value={selectedMonth}
                onChange={setSelectedMonth}
                options={monthOptions}
                styles={{
                  control: (base) => ({
                    ...base,
                    minWidth: 120,
                    borderRadius: 6,
                    fontSize: "1rem",
                    background: "#fff",
                    border: "1.5px solid #d1d5db",
                  }),
                  menu: (base) => ({ ...base, zIndex: 20 }),
                }}
                isClearable
              />
            </div>
            <div className="date-input-wrapper">
              {!selectedYear && (
                <span className="placeholder-text">Year</span>
              )}
              <Select
                placeholder="Select Year"
                // value={selectedYear}
                // onChange={setSelectedYear}
                value={selectedYear} // should be the full object: { value: 2025, label: "2025" }
                onChange={(selectedOption) => setSelectedYear(selectedOption)}
                options={yearOptions}
                styles={{
                  control: (base) => ({
                    ...base,
                    minWidth: 120,
                    borderRadius: 6,
                    fontSize: "1rem",
                    background: "#fff",
                    border: "1.5px solid #d1d5db",
                  }),
                  menu: (base) => ({ ...base, zIndex: 20 }),
                }}
                isClearable
              />
            </div>
          </div>
        </div>
      </div>
      

      
      <div className="reports-table-wrapper">
        {loading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            Loading report data...
          </div>
        ) : (
          <table className="reports-table" data-columns="5">
            <thead>
              <tr>
                <th>S.NO</th>
                <th>Recruiter</th>
                <th>No. of Submissions</th>
                <th>Client</th>
                <th>No. of Requirements Worked</th>
                <th>Candidates Joined</th>
              </tr>
            </thead>
            <tbody>
              
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center" }}>
                    {((selectedMonth && selectedYear) || (!selectedMonth && !selectedYear)) && (
                      <>
                        No reports found for the selected period!
                      </>
                    )}
                    {(selectedMonth && !selectedYear) && (
                      <>
                        Please Select Year
                      </>
                    )}
                    {(!selectedMonth && selectedYear) && (
                      <>
                        Please Select Month
                      </>
                    )}
                   
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr key={item.correl_id || index}>
                    <td>{(page - 1) * ITEMS_PER_PAGE + index + 1}</td>
                    <td>{item.recruiter_name || "--"}</td>
                    <td>{item.no_of_submissions || 0}</td>
                    <td>{item.client_name || '--'}</td>
                    <td>{item.no_of_requirements_worked || 0}</td>
                    <td>{item.candidates_joined || 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {/* Pagination Controls */}
     {pageCount > 1 && selectedMonth && selectedYear && (
  <div className="settings-pagination">
    <button
      className="pagination-btn"
      onClick={() => handlePageChange(page - 1)}
      disabled={page === 1}
    >
      &lt;
    </button>
    {getPageNumbers(page, pageCount, 5).map((pageNum) => (
      <button
        key={pageNum}
        className={"pagination-btn" + (page === pageNum ? " active" : "")}
        onClick={() => handlePageChange(pageNum)}
      >
        {pageNum}
      </button>
    ))}
    <button
      className="pagination-btn"
      onClick={() => handlePageChange(page + 1)}
      disabled={page === pageCount}
    >
      &gt;
    </button>
  </div>
)}
    </div>
  );
}

export default MonthlyReport;
